const API_BASE = import.meta.env.VITE_API_URL || ''

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export class ApiClientError extends Error {
  /** true when the server returned 5xx or the request failed to reach the server */
  readonly isServerUnavailable: boolean

  constructor(
    message: string,
    public code: string | undefined,
    public status: number,
  ) {
    super(message)
    this.name = 'ApiClientError'
    this.isServerUnavailable = status >= 500
  }
}

/** Network-level error (fetch threw before receiving a response) */
export class NetworkError extends Error {
  readonly isServerUnavailable = true
  constructor() {
    super('Network error')
    this.name = 'NetworkError'
  }
}

/**
 * Attempt silent re-auth via Entra ID (prompt=none).
 * Reads tenantSlug from the expired JWT payload to build the authorize URL.
 * If SSO session is still valid, user gets a new auth code without interaction.
 */
async function attemptSilentReAuth(): Promise<void> {
  const token = localStorage.getItem('auth_token')
  if (!token) throw new Error('No token')

  // Decode JWT payload (no verification — just read claims)
  const payload = JSON.parse(atob(token.split('.')[1]))
  const tenantSlug = payload.tenantSlug as string | undefined
  if (!tenantSlug) {
    // Superadmin — no silent re-auth, redirect to login
    throw new Error('No tenant context')
  }

  // Fetch tenant auth config
  const configRes = await fetch(`${API_BASE}/api/auth/tenant-config/${tenantSlug}`)
  if (!configRes.ok) throw new Error('Failed to fetch tenant config')
  const configBody = await configRes.json()
  const cfg = configBody.data.authConfig as { clientId?: string; tenantId?: string }
  if (!cfg?.clientId || !cfg?.tenantId) throw new Error('Auth config incomplete')

  // Generate PKCE and redirect with prompt=none
  const { generatePkce } = await import('./pkce')
  const { codeVerifier, codeChallenge } = await generatePkce()
  sessionStorage.setItem('pkce_code_verifier', codeVerifier)

  const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`)
  const currentPath = window.location.pathname + window.location.search
  const state = encodeURIComponent(`${tenantSlug}:${currentPath}`)
  const params = [
    `client_id=${cfg.clientId}`,
    'response_type=code',
    `redirect_uri=${redirectUri}`,
    'scope=openid%20profile%20email',
    `state=${state}`,
    'response_mode=query',
    `code_challenge=${codeChallenge}`,
    'code_challenge_method=S256',
    'prompt=none',
  ].join('&')

  window.location.href = `https://login.microsoftonline.com/${cfg.tenantId}/oauth2/v2.0/authorize?${params}`
}

export async function handleResponse<T>(res: Response, unwrap = true): Promise<T> {
  if (!res.ok) {
    if (res.status === 401) {
      // Try silent re-auth; fall back to login page
      try {
        await attemptSilentReAuth()
      } catch {
        localStorage.removeItem('auth_token')
        window.location.href = '/login'
      }
      throw new Error('Session expired')
    }
    const body = await res.json().catch(() => ({ error: 'Unknown error' }))
    throw new ApiClientError(body.error || 'Unknown error', body.code, res.status)
  }
  const body = await res.json()
  return unwrap ? (body.data ?? body) : body
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type QueryParams = { [key: string]: string | undefined } | any

export function buildQuery(params?: QueryParams): string {
  if (!params) return ''
  const entries = Object.entries(params).filter(([, v]) => v !== undefined)
  if (entries.length === 0) return ''
  return `?${new URLSearchParams(entries as [string, string][]).toString()}`
}

const API_TIMEOUT_MS = 5000
/** CSVインポートなど、行数に比例して時間がかかる処理向けのタイムアウト */
const LONG_RUNNING_TIMEOUT_MS = 120_000

async function safeFetch(
  input: RequestInfo,
  init?: RequestInit,
  timeoutMs = API_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const res = await fetch(input, { ...init, signal: controller.signal })
    clearTimeout(timeoutId)
    return res
  } catch (e) {
    clearTimeout(timeoutId)
    if ((e as Error).name === 'AbortError') {
      const err = new NetworkError()
      err.message = 'Request timeout'
      throw err
    }
    throw new NetworkError()
  }
}

export interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  pagination: Pagination
}

/** API client wrapping fetch */
export const api = {
  get: async <T = unknown>(path: string, query?: QueryParams): Promise<T> => {
    const res = await safeFetch(`${API_BASE}${path}${buildQuery(query)}`, {
      headers: { ...getAuthHeaders() },
    })
    return handleResponse<T>(res)
  },

  post: async <T = unknown>(path: string, body?: unknown): Promise<T> => {
    const res = await safeFetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: body ? JSON.stringify(body) : undefined,
    })
    return handleResponse<T>(res)
  },

  patch: async <T = unknown>(path: string, body?: unknown): Promise<T> => {
    const res = await safeFetch(`${API_BASE}${path}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: body ? JSON.stringify(body) : undefined,
    })
    return handleResponse<T>(res)
  },

  put: async <T = unknown>(path: string, body?: unknown): Promise<T> => {
    const res = await safeFetch(`${API_BASE}${path}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: body ? JSON.stringify(body) : undefined,
    })
    return handleResponse<T>(res)
  },

  delete: async <T = unknown>(path: string): Promise<T> => {
    const res = await safeFetch(`${API_BASE}${path}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    })
    return handleResponse<T>(res)
  },

  /** Full response (no body.data unwrap) — for paginated list endpoints */
  getFull: async <T = unknown>(path: string, query?: QueryParams): Promise<T> => {
    const res = await safeFetch(`${API_BASE}${path}${buildQuery(query)}`, {
      headers: { ...getAuthHeaders() },
    })
    return handleResponse<T>(res, false)
  },

  /**
   * テキスト本文（CSV/TSV）をそのままPOSTする。
   * 行数に比例して処理時間が伸びるため、通常より長いタイムアウトを使う。
   */
  postRaw: async <T = unknown>(
    path: string,
    body: string,
    contentType = 'text/csv; charset=utf-8',
  ): Promise<T> => {
    const res = await safeFetch(
      `${API_BASE}${path}`,
      {
        method: 'POST',
        headers: { 'Content-Type': contentType, ...getAuthHeaders() },
        body,
      },
      LONG_RUNNING_TIMEOUT_MS,
    )
    return handleResponse<T>(res)
  },

  /** Raw fetch for non-JSON responses (e.g., CSV export) */
  raw: async (path: string, query?: QueryParams): Promise<Response> => {
    return safeFetch(`${API_BASE}${path}${buildQuery(query)}`, {
      headers: { ...getAuthHeaders() },
    })
  },
}
